import {
  adminBffFetch,
  createAgent,
  type getAgentResponse,
  getGetAgentUrl,
  getListAgentsUrl,
  type listAgentsResponse,
  updateAgent,
} from '@avila-roffe/api-clients-admin-bff';
import { toDeploymentStatus, toReleaseStatus } from './release-deployment-utils';
import type {
  Agent,
  AgentDeployment,
  AgentRelease,
  CreateAgentDto,
  CreateDeploymentDto,
  CreateModelAliasDto,
  CreateReleaseDto,
  ModelAlias,
  ModelRegistryEntry,
  UpdateAgentBehaviorDto,
  UpdateAgentDto,
} from './types';

function toRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function isEmptyRecord(value: Record<string, unknown>): boolean {
  return Object.keys(value).length === 0;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function toAgentScope(value: unknown): Agent['scope'] {
  return value === 'PUBLIC' || value === 'TENANT' || value === 'APPLICATION'
    ? value
    : 'APPLICATION';
}

function toAgentType(value: unknown): Agent['type'] {
  return value === 'TASK' || value === 'CONVERSATIONAL' ? value : 'CONVERSATIONAL';
}

function toAgentStatus(value: unknown): Agent['status'] {
  return value === 'ACTIVE' || value === 'DRAFT' || value === 'DEPRECATED' ? value : 'DRAFT';
}

function normalizeAgent(raw: unknown): Agent {
  const row = toRecord(raw);
  const iface = toRecord(row.interface);
  const guardrails = toRecord(row.guardrails);
  const llmConfigRaw = row.llmConfig;

  return {
    id: String(row.id ?? ''),
    appSlug: String(row.appSlug ?? '_admin'),
    name: String(row.name ?? ''),
    tenantSlug: typeof row.tenantSlug === 'string' ? row.tenantSlug : null,
    scope: toAgentScope(row.scope),
    type: toAgentType(row.type),
    status: toAgentStatus(row.status),
    modelAlias: String(row.modelAlias ?? ''),
    systemPrompt: String(row.systemPrompt ?? ''),
    version: String(row.version ?? '1.0.0'),
    interface: {
      displayName: String(iface.displayName ?? ''),
      shortDescription: String(iface.shortDescription ?? ''),
      ...(typeof iface.defaultPrompt === 'string' && { defaultPrompt: iface.defaultPrompt }),
      ...(typeof iface.initialSalutation === 'boolean' && {
        initialSalutation: iface.initialSalutation,
      }),
      ...(Array.isArray(iface.salutationExamples) && {
        salutationExamples: toStringArray(iface.salutationExamples),
      }),
    },
    guardrails: {
      approvalMode:
        guardrails.approvalMode === 'NONE' ||
        guardrails.approvalMode === 'REQUIRED' ||
        guardrails.approvalMode === 'SUGGESTED'
          ? guardrails.approvalMode
          : 'SUGGESTED',
      maxToolCalls:
        typeof guardrails.maxToolCalls === 'number' && Number.isFinite(guardrails.maxToolCalls)
          ? guardrails.maxToolCalls
          : 20,
      maxSteps:
        typeof guardrails.maxSteps === 'number' && Number.isFinite(guardrails.maxSteps)
          ? guardrails.maxSteps
          : 50,
      ...(Array.isArray(guardrails.allowedTools) && {
        allowedTools: toStringArray(guardrails.allowedTools),
      }),
      ...(Array.isArray(guardrails.requiredCapabilities) && {
        requiredCapabilities: toStringArray(guardrails.requiredCapabilities),
      }),
    },
    llmConfig:
      typeof llmConfigRaw === 'object' && llmConfigRaw !== null
        ? {
            ...(typeof (llmConfigRaw as Record<string, unknown>).temperature === 'number' && {
              temperature: (llmConfigRaw as Record<string, unknown>).temperature as number,
            }),
            ...(typeof (llmConfigRaw as Record<string, unknown>).maxTokens === 'number' && {
              maxTokens: (llmConfigRaw as Record<string, unknown>).maxTokens as number,
            }),
            ...(typeof (llmConfigRaw as Record<string, unknown>).topP === 'number' && {
              topP: (llmConfigRaw as Record<string, unknown>).topP as number,
            }),
            ...(Array.isArray((llmConfigRaw as Record<string, unknown>).stopSequences) && {
              stopSequences: toStringArray(
                (llmConfigRaw as Record<string, unknown>).stopSequences as unknown,
              ),
            }),
          }
        : null,
    skillRefs: toStringArray(row.skillRefs),
    workflowRefs: toStringArray(row.workflowRefs),
    toolRefs: toStringArray(row.toolRefs),
    tags: toStringArray(row.tags),
    iconUrl: typeof row.iconUrl === 'string' ? row.iconUrl : null,
    ...(typeof row.createdBy === 'string' && { createdBy: row.createdBy }),
    ...(typeof row.updatedBy === 'string' && { updatedBy: row.updatedBy }),
    createdAt: String(row.createdAt ?? new Date(0).toISOString()),
    updatedAt: String(row.updatedAt ?? new Date(0).toISOString()),
  };
}

function extractAgentRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;

  const root = toRecord(payload);
  const level1 = root.data;

  if (Array.isArray(level1)) return level1;

  const level1Record = toRecord(level1);
  const level2 = level1Record.data;

  if (Array.isArray(level2)) return level2;

  const level2Record = toRecord(level2);

  if (Array.isArray(level2Record.items)) return level2Record.items as unknown[];
  if (Array.isArray(level1Record.items)) return level1Record.items as unknown[];

  return [];
}

export async function listAgentsForApp(appSlug?: string): Promise<Agent[]> {
  const params: Record<string, string> = { limit: '100' };
  if (appSlug) params.appSlug = appSlug;
  const query = new URLSearchParams(params);
  const response = await adminBffFetch<listAgentsResponse>(
    `${getListAgentsUrl()}?${query.toString()}`,
    {
      method: 'GET',
    },
  );

  return extractAgentRows(response.data).map(normalizeAgent);
}

export async function getAgentById(id: string): Promise<Agent | null> {
  const response = await adminBffFetch<getAgentResponse>(getGetAgentUrl(id), { method: 'GET' });
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAgent(data.data ?? data);
}

export async function getAgentByName(name: string, appSlug?: string): Promise<Agent | null> {
  const agents = await listAgentsForApp(appSlug);
  return agents.find((a) => a.name === name) ?? null;
}

export async function createAgentForApp(dto: CreateAgentDto): Promise<Agent | null> {
  const response = await createAgent(dto as unknown as Record<string, unknown>);
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAgent(data.data ?? data);
}

export async function updateAgentByName(name: string, dto: UpdateAgentDto): Promise<Agent | null> {
  const response = await updateAgent(name, dto as unknown as Record<string, unknown>);
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAgent(data.data ?? data);
}

export async function updateAgentById(agentId: string, dto: UpdateAgentDto): Promise<Agent | null> {
  const response = await updateAgent(agentId, dto as unknown as Record<string, unknown>);
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAgent(data.data ?? data);
}

export async function updateAgentBehaviorById(
  agentId: string,
  dto: UpdateAgentBehaviorDto,
): Promise<Agent | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentId)}/behavior`,
    { method: 'PATCH', body: JSON.stringify(dto), headers: { 'Content-Type': 'application/json' } },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAgent(data.data ?? data);
}

export async function deleteAgentByName(agentId: string): Promise<void> {
  await adminBffFetch<unknown>(`/api/v1/agents/${encodeURIComponent(agentId)}`, {
    method: 'DELETE',
  });
}

export async function uploadAgentAvatar(
  name: string,
  appSlug: string,
  file: File,
): Promise<Agent | null> {
  const form = new FormData();
  form.append('file', file);
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(appSlug)}/${encodeURIComponent(name)}/avatar`,
    { method: 'PUT', body: form },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  return normalizeAgent(data.data ?? data);
}

export async function getAgentReleases(
  agentName: string,
  appSlug?: string,
): Promise<AgentRelease[]> {
  const base = `/api/v1/agents/${encodeURIComponent(agentName)}/releases`;
  const url = appSlug ? `${base}?app=${encodeURIComponent(appSlug)}` : base;
  const response = await adminBffFetch<unknown>(url, {
    method: 'GET',
  });
  const root = toRecord(response);
  const dataField = root.data;
  const rows: unknown[] = Array.isArray(dataField)
    ? dataField
    : Array.isArray(toRecord(dataField).data)
      ? (toRecord(dataField).data as unknown[])
      : [];
  return rows.map((r) => normalizeRelease(r));
}

export async function getAgentDeployments(
  agentName: string,
  appSlug?: string,
): Promise<AgentDeployment[]> {
  const params = new URLSearchParams({ name: agentName });
  if (appSlug) params.set('app', appSlug);
  const response = await adminBffFetch<unknown>(`/api/v1/deployments?${params.toString()}`, {
    method: 'GET',
  });
  const root = toRecord(response);
  const dataField = root.data;
  const rows: unknown[] = Array.isArray(dataField)
    ? dataField
    : Array.isArray(toRecord(dataField).data)
      ? (toRecord(dataField).data as unknown[])
      : Array.isArray(toRecord(toRecord(dataField).data).items)
        ? (toRecord(toRecord(dataField).data).items as unknown[])
        : [];
  return rows.map((r) => {
    const row = toRecord(r);
    const agentField = toRecord(row.agent);
    return {
      id: String(row.id ?? ''),
      agentId: typeof row.agentId === 'string' ? row.agentId : undefined,
      agentName: typeof agentField.name === 'string' ? agentField.name : undefined,
      applicationSlug: String(row.applicationSlug ?? ''),
      environmentSlug: String(row.environmentSlug ?? ''),
      tenantSlug: typeof row.tenantSlug === 'string' ? row.tenantSlug : null,
      promptReleaseId: typeof row.promptReleaseId === 'string' ? row.promptReleaseId : null,
      status: toDeploymentStatus(row.status),
      activatedAt: typeof row.activatedAt === 'string' ? row.activatedAt : null,
      createdAt: String(row.createdAt ?? new Date(0).toISOString()),
    };
  });
}

// ─── Release actions ──────────────────────────────────────────────────────────

function normalizeRelease(raw: unknown): AgentRelease {
  const row = toRecord(raw);
  const snap = row.snapshot != null ? toRecord(row.snapshot) : null;
  return {
    id: String(row.id ?? ''),
    agentId: typeof row.agentId === 'string' ? row.agentId : undefined,
    name: String(row.name ?? ''),
    status: toReleaseStatus(row.status),
    publishedAt: typeof row.publishedAt === 'string' ? row.publishedAt : null,
    deprecatedAt: typeof row.deprecatedAt === 'string' ? row.deprecatedAt : null,
    notes: typeof row.notes === 'string' ? row.notes : null,
    createdBy: typeof row.createdBy === 'string' ? row.createdBy : undefined,
    createdAt: String(row.createdAt ?? new Date(0).toISOString()),
    snapshot: snap
      ? (() => {
          const snapIface = snap.interface != null ? toRecord(snap.interface) : {};
          const initialSalutation =
            typeof snap.initialSalutation === 'boolean'
              ? snap.initialSalutation
              : typeof snapIface.initialSalutation === 'boolean'
                ? snapIface.initialSalutation
                : undefined;
          const salutationExamples = Array.isArray(snap.salutationExamples)
            ? snap.salutationExamples.map(String)
            : Array.isArray(snapIface.salutationExamples)
              ? snapIface.salutationExamples.map(String)
              : undefined;
          return {
            systemPrompt: typeof snap.systemPrompt === 'string' ? snap.systemPrompt : undefined,
            modelAlias: typeof snap.modelAlias === 'string' ? snap.modelAlias : undefined,
            version: typeof snap.version === 'string' ? snap.version : undefined,
            guardrails: snap.guardrails != null ? toRecord(snap.guardrails) : undefined,
            skillRefs: Array.isArray(snap.skillRefs) ? snap.skillRefs.map(String) : undefined,
            toolRefs: Array.isArray(snap.toolRefs) ? snap.toolRefs.map(String) : undefined,
            workflowRefs: Array.isArray(snap.workflowRefs)
              ? snap.workflowRefs.map(String)
              : undefined,
            tags: Array.isArray(snap.tags) ? snap.tags.map(String) : undefined,
            ...(initialSalutation !== undefined && { initialSalutation }),
            ...(salutationExamples !== undefined && { salutationExamples }),
          };
        })()
      : null,
  };
}

export interface UpdateReleaseDto {
  name?: string;
  notes?: string;
  systemPrompt?: string;
  modelAlias?: string;
  guardrails?: Record<string, unknown>;
  skillRefs?: string[];
  toolRefs?: string[];
  workflowRefs?: string[];
  initialSalutation?: boolean;
  salutationExamples?: string[];
}

export async function updateAgentRelease(
  agentId: string,
  releaseId: string,
  dto: UpdateReleaseDto,
): Promise<AgentRelease | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentId)}/releases/${encodeURIComponent(releaseId)}`,
    { method: 'PATCH', body: JSON.stringify(dto), headers: { 'Content-Type': 'application/json' } },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeRelease(data.data ?? data);
}

export async function getAgentRelease(
  agentId: string,
  releaseId: string,
): Promise<AgentRelease | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentId)}/releases/${encodeURIComponent(releaseId)}`,
    { method: 'GET' },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeRelease(data.data ?? data);
}

export async function createAgentRelease(
  agentName: string,
  dto: CreateReleaseDto,
): Promise<AgentRelease | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentName)}/releases`,
    { method: 'POST', body: JSON.stringify(dto), headers: { 'Content-Type': 'application/json' } },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeRelease(data.data ?? data);
}

export async function promoteAgentRelease(
  agentName: string,
  releaseId: string,
): Promise<AgentRelease | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentName)}/releases/${encodeURIComponent(releaseId)}/promote`,
    { method: 'POST' },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeRelease(data.data ?? data);
}

export async function publishAgentRelease(
  agentId: string,
  releaseId: string,
): Promise<AgentRelease | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentId)}/releases/${encodeURIComponent(releaseId)}/publish`,
    { method: 'POST' },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeRelease(data.data ?? data);
}

export async function deleteAgentRelease(agentId: string, releaseId: string): Promise<void> {
  await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentId)}/releases/${encodeURIComponent(releaseId)}`,
    { method: 'DELETE' },
  );
}

export async function deprecateAgentRelease(
  agentId: string,
  releaseId: string,
): Promise<AgentRelease | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/agents/${encodeURIComponent(agentId)}/releases/${encodeURIComponent(releaseId)}/deprecate`,
    { method: 'POST' },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeRelease(data.data ?? data);
}

// ─── Deployment actions ───────────────────────────────────────────────────────

function normalizeDeployment(raw: unknown): AgentDeployment {
  const row = toRecord(raw);
  const agentField = toRecord(row.agent);
  return {
    id: String(row.id ?? ''),
    agentId: typeof row.agentId === 'string' ? row.agentId : undefined,
    agentName: typeof agentField.name === 'string' ? agentField.name : undefined,
    applicationSlug: String(row.applicationSlug ?? ''),
    environmentSlug: String(row.environmentSlug ?? ''),
    tenantSlug: typeof row.tenantSlug === 'string' ? row.tenantSlug : null,
    promptReleaseId: typeof row.promptReleaseId === 'string' ? row.promptReleaseId : null,
    status: toDeploymentStatus(row.status),
    activatedAt: typeof row.activatedAt === 'string' ? row.activatedAt : null,
    createdAt: String(row.createdAt ?? new Date(0).toISOString()),
  };
}

export async function activateDeployment(deploymentId: string): Promise<AgentDeployment | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/deployments/${encodeURIComponent(deploymentId)}/activate`,
    { method: 'POST' },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeDeployment(data.data ?? data);
}

export async function createDeployment(dto: CreateDeploymentDto): Promise<AgentDeployment | null> {
  const response = await adminBffFetch<unknown>('/api/v1/deployments', {
    method: 'POST',
    body: JSON.stringify({
      agent_id: dto.agentId,
      prompt_release_id: dto.promptReleaseId,
      application_slug: dto.applicationSlug,
      environment_slug: dto.environmentSlug,
      created_by: dto.createdBy ?? 'admin-portal',
      notes: dto.notes?.trim() ? dto.notes.trim() : undefined,
    }),
  });
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeDeployment(data.data ?? data);
}

export async function undeployDeployment(deploymentId: string): Promise<AgentDeployment | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/deployments/${encodeURIComponent(deploymentId)}/undeploy`,
    { method: 'POST' },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeDeployment(data.data ?? data);
}

// ─── Model aliases ────────────────────────────────────────────────────────────

function normalizeAlias(raw: unknown): ModelAlias {
  const row = toRecord(raw);
  return {
    id: String(row.id ?? ''),
    tenantSlug: String(row.tenantSlug ?? ''),
    alias: String(row.alias ?? ''),
    providerModel: String(row.providerModel ?? ''),
    label: typeof row.label === 'string' ? row.label : null,
    provider: typeof row.provider === 'string' ? row.provider : null,
    isActive: row.isActive !== false,
    createdAt: typeof row.createdAt === 'string' ? row.createdAt : undefined,
    updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : undefined,
  };
}

export async function listTenantAvailableModels(
  tenantSlug: string,
): Promise<{ id: string; label: string; provider: string | null }[]> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantSlug)}/models/available`,
    { method: 'GET' },
  );
  const root = toRecord(response);
  const rows: unknown[] = Array.isArray(root.data) ? root.data : [];
  return rows.flatMap((item) => {
    const r = toRecord(item);
    const name = typeof r.modelName === 'string' ? r.modelName : '';
    if (!name) return [];
    const slash = name.indexOf('/');
    const label = slash === -1 ? name : name.slice(slash + 1);
    const provider = slash === -1 ? null : name.slice(0, slash);
    return [{ id: name, label, provider }];
  });
}

export async function listModelAliases(tenantSlug: string): Promise<ModelAlias[]> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantSlug)}/models/aliases`,
    { method: 'GET' },
  );
  const root = toRecord(response);
  const dataField = root.data;
  const rows: unknown[] = Array.isArray(dataField)
    ? dataField
    : Array.isArray(toRecord(dataField).data)
      ? (toRecord(dataField).data as unknown[])
      : [];
  return rows.map(normalizeAlias);
}

export async function createModelAlias(
  tenantSlug: string,
  dto: CreateModelAliasDto,
): Promise<ModelAlias | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantSlug)}/models/aliases`,
    { method: 'POST', body: JSON.stringify(dto), headers: { 'Content-Type': 'application/json' } },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAlias(data.data ?? data);
}

export async function updateModelAlias(
  tenantSlug: string,
  aliasId: string,
  dto: Partial<CreateModelAliasDto>,
): Promise<ModelAlias | null> {
  const response = await adminBffFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantSlug)}/models/aliases/${encodeURIComponent(aliasId)}`,
    { method: 'PATCH', body: JSON.stringify(dto), headers: { 'Content-Type': 'application/json' } },
  );
  const envelope = toRecord(response);
  const data = toRecord(envelope.data);
  if (isEmptyRecord(data)) return null;
  return normalizeAlias(data.data ?? data);
}

// ─── Model registry ───────────────────────────────────────────────────────────

export async function listModelRegistry(): Promise<ModelRegistryEntry[]> {
  const response = await adminBffFetch<unknown>('/api/v1/models/registry', { method: 'GET' });
  const root = toRecord(response);
  const dataField = root.data;
  const rows: unknown[] = Array.isArray(dataField)
    ? dataField
    : Array.isArray(toRecord(dataField).data)
      ? (toRecord(dataField).data as unknown[])
      : [];
  return rows.map((r) => {
    const row = toRecord(r);
    return {
      id: String(row.id ?? ''),
      modelName: String(row.modelName ?? ''),
      provider: typeof row.provider === 'string' ? row.provider : null,
      displayName: typeof row.displayName === 'string' ? row.displayName : null,
      contextWindow: typeof row.contextWindow === 'number' ? row.contextWindow : null,
      maxOutputTokens: typeof row.maxOutputTokens === 'number' ? row.maxOutputTokens : null,
      inputCostPerMtok: typeof row.inputCostPerMtok === 'number' ? row.inputCostPerMtok : null,
      outputCostPerMtok: typeof row.outputCostPerMtok === 'number' ? row.outputCostPerMtok : null,
      supportsVision: row.supportsVision === true,
      supportsTools: row.supportsTools === true,
      supportsReasoning: row.supportsReasoning === true,
      supportsPromptCaching: row.supportsPromptCaching === true,
      supportsJsonMode: row.supportsJsonMode === true,
      supportsAudioInput: row.supportsAudioInput === true,
      supportsWebSearch: row.supportsWebSearch === true,
      supportsComputerUse: row.supportsComputerUse === true,
      supportsStreaming: row.supportsStreaming === true,
      supportsImageGeneration: row.supportsImageGeneration === true,
      mode: typeof row.mode === 'string' ? row.mode : 'chat',
      isActive: row.isActive !== false,
      syncedAt: String(row.syncedAt ?? ''),
    } satisfies ModelRegistryEntry;
  });
}

export async function syncModelRegistry(): Promise<{ synced: number; syncedAt: string }> {
  const response = await adminBffFetch<unknown>('/api/v1/models/registry/sync', { method: 'POST' });
  const root = toRecord(response);
  const data = toRecord(root.data);
  return {
    synced: typeof data.synced === 'number' ? data.synced : 0,
    syncedAt: String(data.syncedAt ?? ''),
  };
}

export async function deleteModelAlias(tenantSlug: string, aliasId: string): Promise<void> {
  await adminBffFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantSlug)}/models/aliases/${encodeURIComponent(aliasId)}`,
    { method: 'DELETE' },
  );
}
